// =============================================================================
// Apparel sides — typed admin API client
// -----------------------------------------------------------------------------
// Thin typed wrapper around the apparel multi-side admin endpoints. All
// JSON traffic flows through the shared `api` client (cookie + CSRF
// handled by `@tkpod/api-client`). Multipart upload uses raw `fetch`
// because the api-client does not abstract FormData yet — same pattern
// used by `CatalogVariantMatrix` for blank-variant image upload.
// =============================================================================

import { api } from '@/lib/api';
import { getCsrfToken } from '@/lib/csrf';

// -----------------------------------------------------------------------------
// Domain types (mirror server response shape)
// -----------------------------------------------------------------------------

export interface RelativeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * "Scale to product" authoring metadata (mirrors server `ScaleCalibrationDto`).
 *
 * ADMIN-AUTHORING ONLY: lets the editor reopen the calibrated state (real
 * product size + measured reference area) without re-measuring. The
 * renderer/export NEVER read this — they use `printAreaRelative` +
 * `widthIn`/`heightIn`, whose aspect already equals `widthIn:heightIn`.
 * `null` = the side/variant was not authored in "Scale to product" mode.
 *
 * The reference area (`measureRect`) is aspect-locked to
 * `productRealWidthIn:productRealHeightIn`, so a SINGLE uniform scale
 * (`measureRect.width / productRealWidthIn`) is exact on both axes — there is
 * no per-axis ambiguity to resolve.
 */
export interface ScaleCalibration {
  /** Real product width the merchant entered for the reference area. */
  productRealWidthIn: number;
  /** Real product height the merchant entered for the reference area. */
  productRealHeightIn: number;
  /**
   * Relative (0..1) reference area drawn over the product. Its aspect equals
   * `productRealWidthIn:productRealHeightIn` (aspect-locked at author time).
   */
  measureRect: RelativeRect;
}

/**
 * Per-print-area mockup rendering strategy. Mirrors the `PrintAreaMockupMode`
 * enum on the server. See `apps/api/src/modules/catalog/dto/print-area.dto.ts`
 * for full semantics; the UI uses this to:
 *   - render the segmented toggle on each PrintAreaCard
 *   - decide whether to surface the VariantOverridesPanel for this side
 *     (PER_VARIANT only — MASK sides never need per-variant images).
 */
export type PrintAreaMockupMode = 'MASK' | 'PER_VARIANT';

/**
 * Sizing semantics for a print area. Mirrors the server `PrintAreaSizingMode`
 * enum (see `apps/api/src/modules/catalog/dto/print-area.dto.ts`).
 *
 *   - LOCKED (default, legacy): `width`/`height` are the EXACT print size;
 *     the authored rect aspect equals the inch aspect.
 *   - FREE ("free draw"): the rect may take ANY aspect; `width`/`height`
 *     are the MAXIMUM print sheet. The designer derives the effective side
 *     canvas by contain-fitting the rect aspect into the max sheet
 *     (`calcContentSizeIn`), and fulfillment output stays TRIMMED.
 */
export type PrintAreaSizingMode = 'LOCKED' | 'FREE';

export interface PrintAreaSummary {
  id: string;
  slug: string;
  displayName: string;
  width: number;
  height: number;
  positionX: number;
  positionY: number;
  technique: string;
  isPrimary: boolean;
  sortOrder: number;
  mockupImageUrl: string | null;
  printAreaRelative: RelativeRect | null;
  /** Authoring-only "Scale to product" calibration; null when unused. */
  scaleCalibration: ScaleCalibration | null;
  customizationMandatory: boolean;
  maxCanvasIn: number | null;
  mockupMode: PrintAreaMockupMode;
  sizingMode: PrintAreaSizingMode;
  variantConfigCount: number;
}

export interface UpdatePrintAreaPayload {
  mockupImageUrl?: string;
  printAreaRelative?: RelativeRect;
  /** Print area width in inches (1–50). See server `UpdatePrintAreaDto`. */
  widthIn?: number;
  /** Print area height in inches (1–50). */
  heightIn?: number;
  /**
   * Cosmetic side label. The server-side slug stays immutable so S3
   * keys and the [uptId, slug] uniqueness keep their guarantees;
   * displayName only renames the badge in the dnd list and the
   * designer's side switcher.
   */
  displayName?: string;
  customizationMandatory?: boolean;
  /**
   * Optional safe-print limit in inches.
   *
   *   undefined → omit the field from the PATCH body (server keeps the
   *               current value — the universal "no change" channel).
   *   null      → clear the field; server resets the column to NULL so
   *               the designer reverts to its unconstrained default.
   *               Used by the input's blur handler when the merchant
   *               deletes the value.
   *   number    → must be in [0.1, 120]; persisted verbatim.
   */
  maxCanvasIn?: number | null;
  /**
   * Mockup rendering strategy for this print area. Server keeps any
   * existing PrintAreaVariantConfig rows when the mode flips, so toggling
   * back restores the prior state — the admin UI is responsible for
   * confirming with the merchant before issuing the request.
   */
  mockupMode?: PrintAreaMockupMode;
  /**
   * Sizing semantics. Sent by the print-area editor on every save so the
   * persisted mode always matches the mode the merchant authored in.
   */
  sizingMode?: PrintAreaSizingMode;
  /**
   * Authoring-only "Scale to product" calibration.
   *   undefined → omit (server keeps the current value).
   *   null      → clear it (re-authored in a non-scale mode).
   *   object    → persist the calibration.
   */
  scaleCalibration?: ScaleCalibration | null;
}

export interface CreatePrintAreaPayload {
  name: string;
  slug?: string;
  printAreaRelative?: RelativeRect;
  customizationMandatory?: boolean;
  maxCanvasIn?: number;
}

/**
 * Common apparel side presets shown in the "Add Side" dropdown. The slug
 * is the canonical id used by `[uptId, slug]` uniqueness on the server,
 * so the UI uses the same slug to detect "already added" presets.
 */
export interface CommonSidePreset {
  name: string;
  slug: string;
}

export const COMMON_SIDES: CommonSidePreset[] = [
  { name: 'Front', slug: 'front' },
  { name: 'Back', slug: 'back' },
  { name: 'Left Sleeve', slug: 'left-sleeve' },
  { name: 'Right Sleeve', slug: 'right-sleeve' },
  { name: 'Left Chest', slug: 'left-chest' },
  { name: 'Right Chest', slug: 'right-chest' },
  { name: 'Neck Label', slug: 'neck-label' },
  { name: 'Hood', slug: 'hood' },
  { name: 'Pocket', slug: 'pocket' },
];

export interface PrintAreaVariantConfig {
  id?: string;
  blankVariantId: string;
  backgroundColorHex?: string | null;
  backgroundImageUrl?: string | null;
  printAreaOverride?: RelativeRect | null;
  customizationMandatory?: boolean | null;
  /**
   * Per-variant physical print width / height in inches.
   *
   * Server contract (mirrors {@link UpdatePrintAreaPayload.maxCanvasIn}):
   *   undefined → field omitted from the request, server keeps the
   *               persisted value as-is. The "no change" channel.
   *   null      → clear the override; the variant inherits
   *               PrintArea.width / .height for this side.
   *   number    → must be in [0.1, 120]; persisted verbatim.
   *
   * The bulk-upsert endpoint always sends the full canonical state, so
   * `undefined` there is coerced to `null` server-side. The single-row
   * upsert (used by the variant-scoped editor) preserves the
   * `undefined`-vs-`null` distinction.
   */
  printAreaWidthInOverride?: number | null;
  printAreaHeightInOverride?: number | null;
  /**
   * Per-variant "Scale to product" calibration override (authoring-only).
   * `undefined` keeps the persisted value, `null` clears it (variant
   * inherits the side calibration in the editor), an object persists it.
   */
  scaleCalibrationOverride?: ScaleCalibration | null;
}

export interface AutoDetectColorSuggestion {
  blankVariantId: string;
  suggestedHex?: string;
  source: 'OPTION_VALUE_METADATA' | 'NAME_MAP' | 'NONE' | string;
  meta?: Record<string, unknown>;
}

/** A single labelled option-value attached to a blank variant.
 *  Carries `optionTypeSlug='color'` for the apparel "apply by color" flow. */
export interface VariantOptionValueLabel {
  optionTypeId: string | null;
  optionTypeSlug: string | null;
  optionTypeName: string | null;
  value: string;
}

/** Row returned by the variant-config matrix listing endpoint. */
export interface VariantConfigRow {
  blankVariantId: string;
  sku: string;
  blankProductId: string;
  blankProductName: string;
  optionsLabel: string;
  optionValues: VariantOptionValueLabel[];
  /** Convenience: the Color option value, or null when the variant has none. */
  colorOptionValue: string | null;
  currentConfig: {
    /**
     * The persisted PrintAreaVariantConfig row id. `null` while a row is
     * being optimistically created locally (we don't know the cuid until
     * the server replies) — never an empty string. The id is informational;
     * all client-side mutations target the row by `blankVariantId`, so a
     * `null` here never blocks an action.
     */
    id: string | null;
    backgroundColorHex: string | null;
    backgroundImageUrl: string | null;
    printAreaOverride: RelativeRect | null;
    customizationMandatory: boolean | null;
    /**
     * Per-variant physical print width / height in inches.
     * `null` means inherit the side's `PrintArea.width` / `.height`.
     * Surfaced verbatim so the VariantPrintAreaEditorModal can pre-fill
     * the inputs.
     */
    printAreaWidthInOverride: number | null;
    printAreaHeightInOverride: number | null;
    /** Authoring-only "Scale to product" override; null = inherit side. */
    scaleCalibrationOverride: ScaleCalibration | null;
    updatedAt: string;
  } | null;
}

/** Body for the single-row upsert endpoint. Field semantics:
 *  - `undefined` field: leave persisted value untouched.
 *  - `null` (or empty string for hex/url): clear the column. */
export interface UpsertVariantConfigPayload {
  backgroundColorHex?: string | null;
  backgroundImageUrl?: string | null;
  printAreaOverride?: RelativeRect | null;
  customizationMandatory?: boolean | null;
  /** See {@link PrintAreaVariantConfig.printAreaWidthInOverride}. */
  printAreaWidthInOverride?: number | null;
  /** See {@link PrintAreaVariantConfig.printAreaHeightInOverride}. */
  printAreaHeightInOverride?: number | null;
  /** See {@link PrintAreaVariantConfig.scaleCalibrationOverride}. */
  scaleCalibrationOverride?: ScaleCalibration | null;
}

// -----------------------------------------------------------------------------
// Endpoints
// -----------------------------------------------------------------------------

const BASE = '/api/v1/admin/catalog/templates';

export async function listPrintAreas(uptId: string): Promise<PrintAreaSummary[]> {
  const res = await api.get<{ data: PrintAreaSummary[] }>(
    `${BASE}/${uptId}/print-areas`,
  );
  return res.data;
}

export async function createPrintArea(
  uptId: string,
  payload: CreatePrintAreaPayload,
): Promise<PrintAreaSummary> {
  const res = await api.post<{ data: PrintAreaSummary }>(
    `${BASE}/${uptId}/print-areas`,
    payload as unknown as Record<string, unknown>,
  );
  return res.data;
}

export async function deletePrintArea(
  uptId: string,
  printAreaId: string,
): Promise<PrintAreaSummary[]> {
  const res = await api.delete<{ data: PrintAreaSummary[] }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}`,
  );
  return res.data;
}

export async function reorderPrintAreas(
  uptId: string,
  orderedIds: string[],
): Promise<PrintAreaSummary[]> {
  const res = await api.put<{ data: PrintAreaSummary[] }>(
    `${BASE}/${uptId}/print-areas/order`,
    { orderedIds },
  );
  return res.data;
}

export async function updatePrintArea(
  uptId: string,
  printAreaId: string,
  payload: UpdatePrintAreaPayload,
): Promise<PrintAreaSummary> {
  const res = await api.patch<{ data: PrintAreaSummary }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}`,
    payload as unknown as Record<string, unknown>,
  );
  return res.data;
}

export async function uploadPrintAreaMockup(
  uptId: string,
  printAreaId: string,
  file: File,
): Promise<PrintAreaSummary> {
  const baseUrl = import.meta.env.VITE_API_URL as string;
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const url = `${baseUrl}${BASE}/${uptId}/print-areas/${printAreaId}/mockup-upload`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!res.ok) {
    let message = `Mockup upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) message = body.message.join('; ');
      else if (body.message) message = body.message;
    } catch {
      // body not JSON — keep default
    }
    throw new Error(message);
  }

  const json = (await res.json()) as { data: PrintAreaSummary };
  return json.data;
}

export async function bulkUpsertVariantConfigs(
  uptId: string,
  printAreaId: string,
  items: PrintAreaVariantConfig[],
): Promise<PrintAreaVariantConfig[]> {
  const res = await api.put<{ data: PrintAreaVariantConfig[] }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}/variant-configs`,
    { items },
  );
  return res.data;
}

export async function autoDetectApparelColors(
  uptId: string,
  paletteId?: string,
): Promise<AutoDetectColorSuggestion[]> {
  const query = paletteId ? `?paletteId=${encodeURIComponent(paletteId)}` : '';
  const res = await api.post<{ data: AutoDetectColorSuggestion[] }>(
    `${BASE}/${uptId}/apparel/auto-detect-colors${query}`,
    {},
  );
  return res.data;
}

// -----------------------------------------------------------------------------
// Per-variant config (single-row + matrix listing)
// -----------------------------------------------------------------------------

export async function listVariantConfigsForPrintArea(
  uptId: string,
  printAreaId: string,
): Promise<VariantConfigRow[]> {
  const res = await api.get<{ data: VariantConfigRow[] }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}/variant-configs`,
  );
  return res.data;
}

export async function upsertVariantConfig(
  uptId: string,
  printAreaId: string,
  blankVariantId: string,
  payload: UpsertVariantConfigPayload,
): Promise<PrintAreaVariantConfig> {
  const res = await api.put<{ data: PrintAreaVariantConfig }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}/variant-configs/${blankVariantId}`,
    payload as unknown as Record<string, unknown>,
  );
  return res.data;
}

export async function deleteVariantConfig(
  uptId: string,
  printAreaId: string,
  blankVariantId: string,
): Promise<{ deleted: boolean }> {
  const res = await api.delete<{ data: { deleted: boolean } }>(
    `${BASE}/${uptId}/print-areas/${printAreaId}/variant-configs/${blankVariantId}`,
  );
  return res.data;
}

export async function uploadVariantConfigBackground(
  uptId: string,
  printAreaId: string,
  blankVariantId: string,
  file: File,
): Promise<PrintAreaVariantConfig> {
  const baseUrl = import.meta.env.VITE_API_URL as string;
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const url =
    `${baseUrl}${BASE}/${uptId}/print-areas/${printAreaId}` +
    `/variant-configs/${blankVariantId}/background-upload`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (!res.ok) {
    let message = `Background upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) message = body.message.join('; ');
      else if (body.message) message = body.message;
    } catch {
      // body not JSON — keep default
    }
    throw new Error(message);
  }

  const json = (await res.json()) as { data: PrintAreaVariantConfig };
  return json.data;
}
